(() => {
  const SELECTORS = {
    hero: '[data-skeptics-hero]',
    reasons: '[data-skeptics-reasons]',
    reason: '[data-skeptics-reason]',
    reasonClip: '[data-skeptics-reason-clip]',
    progress: '[data-skeptics-progress]',
    label: '[data-skeptics-progress-label]',
    segment: '[data-skeptics-progress-segment]',
    builder: '[data-skeptics-builder], #skeptics-builder',
    builderBox: '.build-a-bundle__info-container'
  };

  let cleanupCurrentPage = null;

  const initialiseBuilderPagination = (builder) => {
    if (!builder || builder.dataset.skepticsPagination !== 'true') return () => {};

    const cardSelector = [
      '.build-a-bundle__product-card',
      '.how-it-works-bab-card',
      '.whats-with-blocks-bab-card',
      '.taste-guarantee-bab-card'
    ].join(',');
    const cards = [...builder.querySelectorAll(cardSelector)];
    const viewMoreButton = builder.querySelector('[data-skeptics-view-more]');

    // The grid is 2 columns on mobile and 3 from the theme's `desktop:`
    // breakpoint up, so the batch size has to change with the breakpoint or
    // "View more" leaves a part-filled last row on desktop. The cards the LP
    // hides in the desktop grid drop out of the count too: counting a
    // display:none card would render a row of 3 as a row of 2.
    const desktopQuery = window.matchMedia(
      `(min-width: ${Math.max(1, Number(builder.dataset.skepticsDesktopBreakpoint) || 1024)}px)`
    );
    let desktopHiddenSelector = (builder.dataset.skepticsDesktopHiddenCards || '').trim();
    if (desktopHiddenSelector) {
      try {
        document.createDocumentFragment().querySelector(desktopHiddenSelector);
      } catch (error) {
        desktopHiddenSelector = '';
      }
    }

    // Each collection's heading sits inside the same grid as its cards but is
    // not a card, so pagination never touched it: with 28 Adults cards ahead of
    // it, every one of the 8 Kids cards fell behind the first-batch cut while
    // the "Kids" title stayed on screen as an orphaned heading above
    // "View more". Group the cards by collection so a heading can be hidden
    // while its whole collection is behind the cut.
    const collections = [...builder.querySelectorAll('.build-a-bundle__collection')]
      .map((root) => ({
        root,
        // The marker is rendered by lp-skeptics-build-a-bundle.liquid; the
        // fallback finds the same wrapper structurally (product cards carry an
        // h3 of their own, so cards have to be excluded) in case the snippet
        // and this asset are ever out of step on the live theme.
        heading: root.querySelector('[data-skeptics-collection-heading]')
          || [...root.children].find((child) => !child.matches(cardSelector) && child.querySelector('h3')),
        cards: cards.filter((card) => root.contains(card)),
        noMatches: root.querySelector('.build-a-bundle__collection-no-filter-matches')
      }))
      .filter((collection) => collection.heading && collection.cards.length);

    const readCount = (value, fallback) => Math.max(1, Number(value) || fallback);

    let pagedCards = cards;
    let initialCount = 10;
    let increment = 10;
    // Counted in clicks, not cards, so a breakpoint change keeps the customer
    // roughly where they were instead of collapsing the grid back to one batch.
    let expansions = 0;
    let renderQueued = false;

    if (!cards.length || !viewMoreButton) return () => {};

    const readBreakpoint = () => {
      const isDesktop = desktopQuery.matches;

      initialCount = isDesktop
        ? readCount(builder.dataset.skepticsInitialCountDesktop, 9)
        : readCount(builder.dataset.skepticsInitialCount, 10);
      increment = isDesktop
        ? readCount(builder.dataset.skepticsCardIncrementDesktop, 9)
        : readCount(builder.dataset.skepticsCardIncrement, 10);
      pagedCards = isDesktop && desktopHiddenSelector
        ? cards.filter((card) => !card.matches(desktopHiddenSelector))
        : cards;
    };

    const render = () => {
      renderQueued = false;
      const visibleLimit = initialCount + (expansions * increment);
      const filterVisibleCards = pagedCards.filter((card) => !card.classList.contains('hidden'));

      cards.forEach((card) => {
        const filteredIndex = filterVisibleCards.indexOf(card);
        const shouldHide = filteredIndex === -1 || filteredIndex >= visibleLimit;
        card.classList.toggle('skeptics-card--page-hidden', shouldHide);
      });

      collections.forEach((collection) => {
        const hasVisibleCard = collection.cards.some((card) => (
          !card.classList.contains('hidden') && !card.classList.contains('skeptics-card--page-hidden')
        ));
        // A collection filtered down to nothing still shows the theme's "no
        // products match" line, and that line needs its title to say which
        // collection it is about. Only a collection emptied by pagination
        // loses its heading.
        const showsNoMatches = collection.noMatches
          ? !collection.noMatches.classList.contains('hidden')
          : false;
        const isOrphaned = !hasVisibleCard && !showsNoMatches;
        collection.heading.classList.toggle('skeptics-collection-heading--page-hidden', isOrphaned);
        collection.root.classList.toggle('skeptics-collection--page-empty', isOrphaned);
      });

      const remainingCount = Math.max(0, filterVisibleCards.length - visibleLimit);
      viewMoreButton.hidden = remainingCount === 0;
      viewMoreButton.textContent = remainingCount > increment
        ? `View ${increment} more`
        : remainingCount > 0
          ? `View ${remainingCount} more`
          : 'View more';
      viewMoreButton.setAttribute('aria-expanded', remainingCount === 0 ? 'true' : 'false');
    };

    const queueRender = () => {
      if (renderQueued) return;
      renderQueued = true;
      window.requestAnimationFrame(render);
    };

    const onViewMore = () => {
      expansions += 1;
      render();
    };

    const onBreakpointChange = () => {
      readBreakpoint();
      render();
    };

    // The Adults/Kids anchor buttons scroll to their collection, but cards
    // beyond the pagination cut are display:none, so the Kids section would
    // measure near-empty and the scroll would land wrong. Capture phase runs
    // on this ancestor BEFORE the theme's own listener on the button itself,
    // so the full grid is laid out by the time the theme computes offsets.
    const onNavCapture = (event) => {
      if (!event.target.closest('.build-a-bundle__sticky-nav-trigger')) return;
      const totalCards = pagedCards.filter((card) => !card.classList.contains('hidden')).length;
      if (initialCount + (expansions * increment) >= totalCards) return;
      expansions = Math.ceil((totalCards - initialCount) / increment);
      render();
    };

    const classObserver = new MutationObserver(queueRender);
    cards.forEach((card) => classObserver.observe(card, { attributes: true, attributeFilter: ['class'] }));
    viewMoreButton.addEventListener('click', onViewMore);
    builder.addEventListener('click', onNavCapture, true);
    if (desktopQuery.addEventListener) desktopQuery.addEventListener('change', onBreakpointChange);
    else if (desktopQuery.addListener) desktopQuery.addListener(onBreakpointChange);
    readBreakpoint();
    render();

    return () => {
      classObserver.disconnect();
      viewMoreButton.removeEventListener('click', onViewMore);
      builder.removeEventListener('click', onNavCapture, true);
      if (desktopQuery.removeEventListener) desktopQuery.removeEventListener('change', onBreakpointChange);
      else if (desktopQuery.removeListener) desktopQuery.removeListener(onBreakpointChange);
      cards.forEach((card) => card.classList.remove('skeptics-card--page-hidden'));
      collections.forEach((collection) => {
        collection.heading.classList.remove('skeptics-collection-heading--page-hidden');
        collection.root.classList.remove('skeptics-collection--page-empty');
      });
    };
  };

  const initialiseGuaranteeQuotes = () => {
    const swipe = document.querySelector('[data-skeptics-guarantee-swipe]');
    const track = swipe?.querySelector('[data-skeptics-guarantee-track]');
    const quotes = swipe ? [...swipe.querySelectorAll('[data-skeptics-guarantee-quote]')] : [];
    const dots = swipe ? [...swipe.querySelectorAll('[data-skeptics-guarantee-dot]')] : [];
    let updateQueued = false;

    if (!track || quotes.length < 2 || quotes.length !== dots.length) return () => {};

    const setActiveDot = (activeIndex) => {
      dots.forEach((dot, index) => {
        const isActive = index === activeIndex;
        dot.classList.toggle('is-active', isActive);
        if (isActive) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    };

    const updateFromScroll = () => {
      updateQueued = false;
      const activeQuote = quotes.reduce((closest, quote, index) => {
        const distance = Math.abs(quote.offsetLeft - track.scrollLeft);
        return distance < closest.distance ? { index, distance } : closest;
      }, { index: 0, distance: Number.POSITIVE_INFINITY });
      setActiveDot(activeQuote.index);
    };

    const onScroll = () => {
      if (updateQueued) return;
      updateQueued = true;
      window.requestAnimationFrame(updateFromScroll);
    };

    const dotHandlers = dots.map((dot, index) => {
      const handler = () => track.scrollTo({
        left: quotes[index].offsetLeft,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
      });
      dot.addEventListener('click', handler);
      return handler;
    });

    track.addEventListener('scroll', onScroll, { passive: true });
    setActiveDot(0);

    return () => {
      track.removeEventListener('scroll', onScroll);
      dots.forEach((dot, index) => dot.removeEventListener('click', dotHandlers[index]));
    };
  };

  const initialiseDelegatedPopups = () => {
    // Rows outside the builder's template root (e.g. the Dragons' Den card's
    // delivery row) cannot be bound by app.js, whose popup triggers are scoped
    // to .build-a-bundle-template. Delegate their clicks to the builder's own
    // trigger so the theme's popup machinery stays the single owner.
    const delegates = [...document.querySelectorAll('[data-skeptics-delegated-popup]')];
    if (!delegates.length) return () => {};

    const handlers = delegates.map((el) => {
      const handler = (event) => {
        const target = el.dataset.skepticsDelegatedPopup;
        const trigger = document.querySelector(`.build-a-bundle-template .${target}-popup-trigger`);
        if (!trigger) return; // no popup on the page: an <a> delegate falls back to its href
        event.preventDefault();
        trigger.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
      };
      el.addEventListener('click', handler);
      return handler;
    });

    return () => {
      delegates.forEach((el, index) => el.removeEventListener('click', handlers[index]));
    };
  };

  const initialiseInfoPopups = () => {
    // The dedicated production Build Your Bundle disclosure popups (taste
    // guarantee + delivery), rendered up front by the prebuilt section with the
    // same class contract as the production snippets. app.js scopes its click
    // bindings to .build-a-bundle-template, which this LP no longer renders, so
    // this copies only its state transition: add .active to open, remove it to
    // close. Ported verbatim from lp-lagomchef.js (the working precedent).
    const popups = [...document.querySelectorAll('[data-skeptics-info-popup]')];
    if (!popups.length) return () => {};

    let activePopup = null;
    let activeTrigger = null;

    const lockBackground = () => {
      if (document.body.style.position === 'fixed') return;
      document.body.classList.add('overflow-y-hidden');
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.overflow = 'hidden';
    };

    const unlockBackground = () => {
      if (document.body.style.position !== 'fixed') return;
      document.body.classList.remove('overflow-y-hidden');
      const scrollY = Math.abs(parseInt(document.body.style.top || 0, 10));
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.overflow = '';
      window.requestAnimationFrame(() => {
        window.scrollTo({ top: scrollY, left: 0, behavior: 'instant' });
      });
    };

    const close = ({ restoreFocus = true } = {}) => {
      if (!activePopup) return;
      activePopup.classList.remove('active');
      activePopup = null;
      unlockBackground();
      if (restoreFocus && activeTrigger?.isConnected) activeTrigger.focus({ preventScroll: true });
      activeTrigger = null;
    };

    const open = (key, trigger) => {
      const popup = popups.find((candidate) => candidate.dataset.skepticsInfoPopup === key);
      if (!popup) return false;
      if (activePopup && activePopup !== popup) activePopup.classList.remove('active');
      activePopup = popup;
      activeTrigger = trigger;
      lockBackground();
      popup.classList.add('active');
      popup.querySelector('button[data-skeptics-info-popup-close]')?.focus({ preventScroll: true });
      return true;
    };

    const onClick = (event) => {
      const trigger = event.target.closest('[data-skeptics-delegated-popup]');
      if (trigger) {
        if (open(trigger.dataset.skepticsDelegatedPopup, trigger)) event.preventDefault();
        return;
      }

      const closeControl = event.target.closest('[data-skeptics-info-popup-close]');
      if (closeControl?.closest('[data-skeptics-info-popup]') === activePopup) close();
    };

    const onKeydown = (event) => {
      if (event.key === 'Escape' && activePopup) close();
    };

    document.addEventListener('click', onClick);
    document.addEventListener('keydown', onKeydown);

    return () => {
      document.removeEventListener('click', onClick);
      document.removeEventListener('keydown', onKeydown);
      close({ restoreFocus: false });
    };
  };

  const initialiseCustomiseBundle = () => {
    // "Customise your bundle" on a prebuilt card seeds the on-page builder with
    // that card's products by replaying clicks on the builder's own +/- controls.
    // Driving the theme's real updateBundle path keeps localStorage, the item
    // counter, milestone rewards, pricing and the progress toasts all correct,
    // so by the time the scroll lands the tracker already shows the seeded
    // bundle. Replace semantics: all decrements run before any increments so
    // the max-items cap can never block a seed.
    const triggers = [...document.querySelectorAll('[data-skeptics-customise-bundle]')];
    if (!triggers.length) return () => {};

    const handlers = triggers.map((trigger) => {
      const handler = (event) => {
        const builder = document.querySelector('.build-a-bundle-template');
        const section = trigger.closest('.section-prebuilt-bundles');
        const contents = section?.querySelector('.bundle-contents.active') || section?.querySelector('.bundle-contents');
        if (!builder || !contents) return; // no builder on the page: the anchor href takes over

        const wanted = new Set(
          [...contents.querySelectorAll('.bundle-contents-product[data-id]')]
            .map((row) => row.dataset.id)
            .filter(Boolean)
        );
        if (!wanted.size) return;

        const cards = [...builder.querySelectorAll('.build-a-bundle__product-card[data-id]')];
        if (!cards.some((card) => wanted.has(card.dataset.id))) return; // none purchasable here: fall back

        event.preventDefault();

        const currentQuantity = (id) => Number(window.bundle?.items?.[id]?.quantity) || 0;
        const clickTimes = (button, times) => {
          for (let i = 0; i < times; i += 1) {
            if (!button || button.disabled) return;
            button.click();
          }
        };

        cards.forEach((card) => {
          const target = wanted.has(card.dataset.id) ? 1 : 0;
          const surplus = currentQuantity(card.dataset.id) - target;
          if (surplus > 0) clickTimes(card.querySelector('.product-quantity__decrease'), surplus);
        });
        cards.forEach((card) => {
          const target = wanted.has(card.dataset.id) ? 1 : 0;
          const deficit = target - currentQuantity(card.dataset.id);
          if (deficit > 0) clickTimes(card.querySelector('.product-quantity__increase'), deficit);
        });

        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        builder.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
      };
      trigger.addEventListener('click', handler);
      return handler;
    });

    return () => {
      triggers.forEach((el, index) => el.removeEventListener('click', handlers[index]));
    };
  };

  const initialiseBundleItemPopups = () => {
    // Tapping an item inside the bundle box opens the same meal popup as the
    // builder's product cards: the tap is relayed to that card's own
    // popup trigger, so the theme's popup machinery stays the single owner.
    // Delegated from the box container because the theme re-renders the item
    // rows on every bundle mutation.
    const builder = document.querySelector('.build-a-bundle-template');
    const box = builder?.querySelector('.build-a-bundle__info-container');
    if (!builder || !box) return () => {};

    const openPopupForVariant = (variantId) => {
      if (!variantId) return;
      const card = builder.querySelector(`.build-a-bundle__product-card[data-id="${variantId}"]`);
      card?.querySelector('.build-a-bundle__product-card__popup-trigger')
        ?.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
    };

    const handler = (event) => {
      if (event.target.closest('button, a')) return; // leave the row's real controls alone

      const mobileItem = event.target.closest('.build-a-bundle__info-body-mobile-product-list-product');
      if (mobileItem) {
        openPopupForVariant(mobileItem.dataset.productId);
        return;
      }

      // The desktop thumbnails carry no id, only the item's image, so match
      // the src back to the bundle entry that rendered it.
      const thumb = event.target.closest('.desktop-bundle-image');
      if (thumb) {
        const src = thumb.querySelector('img')?.getAttribute('src');
        if (!src) return;
        const entry = Object.entries(window.bundle?.items || {}).find(([, item]) => item?.image === src);
        openPopupForVariant(entry?.[0]);
      }
    };

    box.addEventListener('click', handler);
    return () => box.removeEventListener('click', handler);
  };

  const initialiseReasonClips = () => {
    // Each reason's media can be a silent looping clip cut from that reason's
    // own source ad. Playback is scoped to the row being on screen so five
    // videos never decode at once, and the poster (the clip's own first frame)
    // covers every case where playback does not happen: reduced motion, a
    // refused autoplay, a failed load.
    const clips = [...document.querySelectorAll(SELECTORS.reasonClip)];
    if (!clips.length) return () => {};

    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onScreen = new Set();

    const play = (clip) => {
      if (motionQuery.matches) return;
      clip.muted = true; // the attribute alone does not satisfy every autoplay policy
      const started = clip.play();
      started?.catch?.(() => {}); // refusal is fine: the poster stays up
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          onScreen.add(entry.target);
          play(entry.target);
        } else {
          onScreen.delete(entry.target);
          entry.target.pause();
        }
      });
    }, { threshold: 0.25 });

    clips.forEach((clip) => {
      clip.muted = true;
      observer.observe(clip);
    });

    // A backgrounded tab gets no intersection callbacks, so pause on the way
    // out and restart whatever was on screen on the way back in.
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') clips.forEach((clip) => clip.pause());
      else onScreen.forEach(play);
    };

    const onMotionChange = () => {
      if (motionQuery.matches) clips.forEach((clip) => clip.pause());
      else onScreen.forEach(play);
    };

    document.addEventListener('visibilitychange', onVisibility);
    motionQuery.addEventListener?.('change', onMotionChange);

    return () => {
      observer.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
      motionQuery.removeEventListener?.('change', onMotionChange);
    };
  };


  // Lazy videos (2026-09-02 image-loading audit). The theme's stocked.food.custom.js
  // force-plays every <video> on the page at DOMContentLoaded, so with 30 clips on
  // the Families LP a phone pulled 112MB of video in 15 seconds before any tap and
  // image requests to the same CDN host queued behind the streams (the "absolute
  // chaos" accordion photos never arrived in WebKit). Sections now hold sources in
  // data-src with preload="none"; this attaches and plays a clip only when it is
  // near the viewport and pauses it when it leaves. load() after attaching resets
  // paused to true and rejects the theme's pending play() promise, so a clip never
  // starts on its own the moment a source appears. Hidden desktop duplicates never
  // intersect on a phone, so they never load at all.

  // Image warm-up (2026-09-02, Tim on an iPhone: accordion photos arrived late on
  // open and most images landed after they were on screen). Native loading="lazy"
  // is what keeps hidden desktop duplicates and closed popups from loading, but
  // Safari's own lazy distance is short, and no observer margin can reach a slide
  // parked off-screen inside a horizontal carousel: the carousel's own overflow
  // clips it out of every intersection test. So the trigger is the SECTION: once a
  // section is within 1200px of the viewport, every lazy image in it that is
  // actually displayed flips to eager (verified in WebKit: changing loading to
  // eager starts the fetch). display:none duplicates and closed popups have no
  // offsetParent, stay lazy, and load only when shown, so the load-time saving is
  // untouched; everything a reader is about to reach is already fetched, and a
  // collapsed accordion photo is ready before the item opens.
  const initialiseImageWarmup = () => {
    if (!('IntersectionObserver' in window)) return () => {};
    const sections = [...document.querySelectorAll('.shopify-section')].filter((section) => section.querySelector('img[loading="lazy"]'));
    if (!sections.length) return () => {};
    const wake = (section) => {
      section.querySelectorAll('img[loading="lazy"]').forEach((image) => {
        if (image.offsetParent) image.loading = 'eager';
      });
    };
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) wake(entry.target);
      });
    }, { rootMargin: '1200px 0px' });
    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  };

  const initialiseLazyVideos = () => {
    const videos = [...document.querySelectorAll('video[data-lp-lazy-video]')];
    if (!videos.length) return () => {};

    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    // Two steps, deliberately. The poster is cheap and is what a reader sees first,
    // so every displayed clip in a section gets it as soon as the section is near.
    // Sources are attached only when the clip itself is in view: with HLS sources
    // WebKit buffers an attached clip whether or not it is playing, and attaching
    // a whole carousel at once made ten seconds on Using your Blocks pull ~100MB
    // (measured 2026-09-02). Poster first, sources on intersection, load() after
    // the sources so the theme's pending play() promise is rejected and nothing
    // starts on its own.
    // The content-links hover clips exist for the desktop mouseenter effect only
    // (the theme's own handler gates on innerWidth > 1023); on a phone they sit at
    // opacity 0 under the main image, so attaching them there is pure waste.
    const isPhoneOnlyHidden = (video) => video.classList.contains('hover-video') && window.innerWidth <= 1023;

    const attachPoster = (video) => {
      if (isPhoneOnlyHidden(video)) return;
      if (video.dataset.lpPoster === 'true') return;
      video.dataset.lpPoster = 'true';
      if (video.dataset.poster) video.setAttribute('poster', video.dataset.poster);
    };

    const attach = (video) => {
      if (isPhoneOnlyHidden(video)) return;
      attachPoster(video);
      if (video.dataset.lpAttached === 'true') return;
      video.dataset.lpAttached = 'true';
      video.querySelectorAll('source[data-src]').forEach((source) => {
        source.setAttribute('src', source.dataset.src);
      });
      video.load();
    };

    const play = (video) => {
      if (video.dataset.lpAutoplay === undefined || motionQuery.matches) return;
      video.muted = true;
      const started = video.play();
      started?.catch?.(() => {});
    };

    if (!('IntersectionObserver' in window)) {
      videos.forEach((video) => { attach(video); play(video); });
      return () => {};
    }

    // Reaching one clip attaches sources and poster to every displayed clip in
    // the same section. A carousel's off-screen slides are clipped by its own
    // overflow, so no margin ever reaches them (found on an iPhone, 2026-09-02:
    // the Using your Blocks slides to the right never loaded). Attaching costs
    // nothing but the poster with preload="none"; only play() fetches, and that
    // still happens per clip, when it is actually intersecting.
    const attachGroup = (video) => {
      const section = video.closest('.shopify-section');
      if (!section) return;
      section.querySelectorAll('video[data-lp-lazy-video]').forEach((other) => {
        if (other.offsetParent) attachPoster(other);
      });
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const video = entry.target;
        if (entry.isIntersecting) {
          attach(video);
          attachGroup(video);
          play(video);
        } else if (!video.paused) {
          video.pause();
        }
      });
    }, { rootMargin: '800px 0px' });

    videos.forEach((video) => observer.observe(video));

    // Section-level safety net (2026-09-02, Tim on an iPhone: Using your Blocks and
    // the footer clip showed nothing on the Skeptics LP while Playwright's WebKit
    // showed them fine). Anything that creates <video> elements after boot, a
    // carousel cloning its slides on a real touch device for example, leaves
    // clips this observer has never seen. So each section that holds lazy clips
    // is watched too: when it comes within 800px, every lazy clip inside it,
    // clones included, is attached, and any clip not yet observed is handed to the
    // per-clip observer above so playback still follows the viewport.
    const observed = new WeakSet(videos);
    const sections = [...new Set(videos.map((video) => video.closest('.shopify-section')).filter(Boolean))];
    const sectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.querySelectorAll('video[data-lp-lazy-video]').forEach((video) => {
          if (video.offsetParent) attachPoster(video);
          if (!observed.has(video)) {
            observed.add(video);
            observer.observe(video);
          }
        });
      });
    }, { rootMargin: '800px 0px' });
    sections.forEach((section) => sectionObserver.observe(section));

    return () => {
      observer.disconnect();
      sectionObserver.disconnect();
    };
  };


  // Opt-in on-device diagnostics (2026-09-02): open the page with ?lpdebug=1 and
  // every uncaught error and rejected promise is printed in a fixed overlay, so a
  // phone-only failure can be read without a cable. Inert without the parameter.
  const lpDebug = /[?&]lpdebug=1/.test(window.location.search);
  const lpReport = (label, error) => {
    if (!lpDebug) return;
    let box = document.getElementById('lp-debug');
    if (!box) {
      box = document.createElement('pre');
      box.id = 'lp-debug';
      box.style.cssText = 'position:fixed;left:0;right:0;bottom:0;z-index:2147483647;max-height:40vh;overflow:auto;margin:0;padding:8px;background:#111;color:#0f0;font:11px/1.4 monospace;white-space:pre-wrap;';
      document.body.appendChild(box);
    }
    const message = error && (error.stack || error.message) ? (error.stack || error.message) : String(error);
    box.textContent += '[' + label + '] ' + message + '\n';
  };
  if (lpDebug) {
    window.addEventListener('error', (event) => lpReport('error', event.error || event.message));
    window.addEventListener('unhandledrejection', (event) => lpReport('rejection', event.reason));
    document.addEventListener('DOMContentLoaded', () => lpReport('boot', 'script ran, UA ' + navigator.userAgent.slice(0, 80)));
  }

  // One initialiser failing must not take the others with it: run each in its own
  // try/catch and return a no-op cleanup for the one that failed.
  const guarded = (label, initialise) => {
    try {
      return initialise() || (() => {});
    } catch (error) {
      lpReport(label, error);
      return () => {};
    }
  };

  const initialiseSkepticsPage = () => {
    if (cleanupCurrentPage) cleanupCurrentPage();

    const hero = document.querySelector(SELECTORS.hero);
    const reasonsRoot = document.querySelector(SELECTORS.reasons);
    const progress = reasonsRoot?.querySelector(SELECTORS.progress);
    const reasons = reasonsRoot ? [...reasonsRoot.querySelectorAll(SELECTORS.reason)] : [];

    if (!hero || !reasonsRoot || !progress || !reasons.length) return;

    if (progress.parentElement !== document.body) document.body.append(progress);

    const label = progress.querySelector(SELECTORS.label);
    const segments = [...progress.querySelectorAll(SELECTORS.segment)];
    const builder = document.querySelector(SELECTORS.builder);
    const cleanupPagination = guarded('builderPagination', () => initialiseBuilderPagination(builder));
    const cleanupGuaranteeQuotes = guarded('guaranteeQuotes', initialiseGuaranteeQuotes);
    const cleanupDelegatedPopups = guarded('delegatedPopups', initialiseDelegatedPopups);
    const cleanupInfoPopups = guarded('infoPopups', initialiseInfoPopups);
    const cleanupCustomiseBundle = guarded('customiseBundle', initialiseCustomiseBundle);
    const cleanupBundleItemPopups = guarded('bundleItemPopups', initialiseBundleItemPopups);
    const cleanupReasonClips = guarded('reasonClips', initialiseReasonClips);
    const mediaQuery = window.matchMedia('(max-width: 1024px)');
    let currentReason = 1;
    let heroIsVisible = true;
    let reasonsInView = false;
    let builderIsVisible = false;
    let scrollTickQueued = false;

    const setActiveReason = (nextIndex) => {
      currentReason = Math.max(1, Math.min(reasons.length, nextIndex));
      if (label) label.textContent = `Reason ${currentReason} of ${reasons.length}`;

      segments.forEach((segment, segmentIndex) => {
        const displayedIndex = segmentIndex + 1;
        segment.classList.toggle('is-active', displayedIndex === currentReason);
        segment.classList.toggle('is-complete', displayedIndex < currentReason);
        if (displayedIndex === currentReason) segment.setAttribute('aria-current', 'step');
        else segment.removeAttribute('aria-current');
      });
    };

    // The active reason is the last one whose top has crossed a fixed trigger
    // line, recomputed from geometry on every scroll frame. A pure function of
    // scroll position updates in both directions with no ratchet, and with
    // reasons around a viewport tall a single line needs no hysteresis: the
    // label can only flicker on sub-pixel jitter exactly at a boundary.
    // The midline, not the scrollspy-conventional near-top (~25%) or the
    // viewport bottom: a reason goes active as it takes over the upper half of
    // the screen, where mobile reading attention sits, so the counter agrees
    // with what is under the eye at the moment it changes; the bottom edge
    // would show "5 of 5" while reason 4 still fills the screen. Measured
    // against visualViewport so the mobile address-bar collapse doesn't drift
    // the line.
    const REASON_TRIGGER_LINE = 0.5;

    const syncActiveReason = () => {
      const viewportHeight = window.visualViewport?.height || window.innerHeight;
      const triggerY = viewportHeight * REASON_TRIGGER_LINE;
      let nextIndex = 1;
      reasons.forEach((reason, index) => {
        if (reason.getBoundingClientRect().top <= triggerY) {
          nextIndex = Number(reason.dataset.reasonIndex) || index + 1;
        }
      });
      setActiveReason(nextIndex);
    };

    const syncProgressVisibility = () => {
      // The bar lives and dies with the reasons section: visible only while some
      // part of it is on screen. The hero gate stops a flash at page top, where
      // the hero and the first reason can share the viewport.
      const shouldShow = reasonsInView && !heroIsVisible;
      progress.classList.toggle('is-visible', shouldShow);
      progress.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
    };

    const bundleHasItems = () => {
      const items = window.bundle?.items;
      if (!items) return false;
      return Object.values(items).some((item) => Number(item?.quantity) > 0);
    };

    // The theme's expanded bundle box locks the page (body position:fixed via
    // its lockBackground), and only its own close path unlocks it. So the box
    // must never be hidden while expanded: close it through the theme's own
    // trigger first, and if that somehow fails, leave the box visible rather
    // than strand the shopper on an unscrollable page. (Bug found by Tim:
    // emptying the bundle from inside the expanded box hid it mid-lock.)
    const closeExpandedBuilderBox = (builderBox) => {
      if (!builderBox.classList.contains('active')) return true;
      const trigger = builder.querySelector('.build-a-bundle__info-expand-trigger')
        || builder.querySelector('.build-a-bundle__info-header-trigger');
      trigger?.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
      return !builderBox.classList.contains('active');
    };

    const syncBuilderBox = () => {
      if (!builder) return;
      const builderBox = builder.querySelector(SELECTORS.builderBox);
      if (!builderBox) return;

      if (mediaQuery.matches) {
        // Once the bundle holds anything, the sticky box stays visible across
        // the whole page so the shopper never loses their bundle; empty
        // bundles keep the original builder-in-view gating.
        const shouldShow = builderIsVisible || bundleHasItems();
        if (!shouldShow && !closeExpandedBuilderBox(builderBox)) return;
        builderBox.classList.toggle('skeptics-builder-box--in-view', shouldShow);
      } else {
        if (!closeExpandedBuilderBox(builderBox)) return;
        builderBox.classList.remove('skeptics-builder-box--in-view');
      }
    };

    const heroObserver = new IntersectionObserver(([entry]) => {
      heroIsVisible = entry.isIntersecting;
      syncProgressVisibility();
    }, { threshold: 0.05 });

    const reasonsRootObserver = new IntersectionObserver(([entry]) => {
      reasonsInView = entry.isIntersecting;
      if (reasonsInView) syncActiveReason();
      syncProgressVisibility();
    }, { threshold: 0 });

    const builderObserver = builder ? new IntersectionObserver(([entry]) => {
      builderIsVisible = entry.isIntersecting;
      syncBuilderBox();
    }, { threshold: 0.02 }) : null;

    // The theme re-renders the bundle box's contents on every bundle mutation
    // (adds, removes, localStorage preload, the customise-bundle seeding), so
    // its subtree changing is the signal that bundleHasItems() may have
    // flipped. syncBuilderBox only toggles a class on the box element itself,
    // which this observer does not watch, so no feedback loop.
    const builderBoxElement = builder?.querySelector(SELECTORS.builderBox);
    const builderBoxObserver = builderBoxElement ? new MutationObserver(syncBuilderBox) : null;
    builderBoxObserver?.observe(builderBoxElement, { childList: true, subtree: true, characterData: true });

    const onScroll = () => {
      if (!reasonsInView || scrollTickQueued) return;
      scrollTickQueued = true;
      window.requestAnimationFrame(() => {
        scrollTickQueued = false;
        syncActiveReason();
      });
    };

    heroObserver.observe(hero);
    reasonsRootObserver.observe(reasonsRoot);
    if (builderObserver && builder) builderObserver.observe(builder);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    const onViewportChange = () => syncBuilderBox();
    mediaQuery.addEventListener?.('change', onViewportChange);
    syncActiveReason();
    syncProgressVisibility();
    syncBuilderBox();

    cleanupCurrentPage = () => {
      heroObserver.disconnect();
      reasonsRootObserver.disconnect();
      builderObserver?.disconnect();
      builderBoxObserver?.disconnect();
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      mediaQuery.removeEventListener?.('change', onViewportChange);
      cleanupPagination();
      cleanupGuaranteeQuotes();
      cleanupDelegatedPopups();
      cleanupInfoPopups();
      cleanupCustomiseBundle();
      cleanupBundleItemPopups();
      cleanupReasonClips();
      if (reasonsRoot.isConnected) reasonsRoot.append(progress);
      else progress.remove();
      cleanupCurrentPage = null;
    };
  };

  // Media loading boots on its own (2026-09-02). It used to run inside
  // initialiseSkepticsPage, after seven other initialisers and behind that
  // function's early return, so on a phone where anything earlier threw, the
  // Using your Blocks clips and the footer clip were blank boxes on this LP while
  // Families, whose media init already ran standalone, was fine. Same shape as
  // Families' initialiseStandalone now.
  let cleanupMedia = null;
  const initialiseMedia = () => {
    cleanupMedia?.();
    const cleanups = [guarded('lazyVideos', initialiseLazyVideos), guarded('imageWarmup', initialiseImageWarmup)];
    cleanupMedia = () => cleanups.forEach((cleanup) => cleanup?.());
  };

  const boot = () => {
    initialiseMedia();
    initialiseSkepticsPage();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('shopify:section:unload', () => cleanupCurrentPage?.());
})();
